import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { publishableKeyFromHost } from '@clerk/react/internal';
import { shadcn } from '@clerk/themes';
import { Switch, Route, useLocation, Router as WouterRouter, Redirect, Link } from 'wouter';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Accessibility } from 'lucide-react';
import HomePage from '@/pages/HomePage';
import DashboardPage from '@/pages/DashboardPage';
import SitesPage from '@/pages/SitesPage';
import SiteDetailPage from '@/pages/SiteDetailPage';
import ScanReportPage from '@/pages/ScanReportPage';
import BillingPage from '@/pages/BillingPage';
import SettingsPage from '@/pages/SettingsPage';
import RefundPage from '@/pages/RefundPage';
import TermsPage from '@/pages/TermsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import NotFound from '@/pages/not-found';
import { ClerkProvider, SignIn, SignUp, useAuth, useClerk } from '@clerk/react';
import { useToast } from '@/hooks/use-toast';
import {
  createSite,
  getGetLegalConsentStatusQueryKey,
  runScan,
  useAcceptLegalConsent,
  useGetLegalConsentStatus,
} from '@workspace/api-client-react';
import { useEffect, useRef, useState } from 'react';
import { ToastAction } from '@/components/ui/toast';

const queryClient = new QueryClient();

const clerkPubKey = publishableKeyFromHost(
  window.location.hostname,
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
);

const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;

const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || '/'
    : path;
}

if (!clerkPubKey) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY in .env file');
}

const clerkAppearance = {
  theme: shadcn,
  cssLayerName: 'clerk',
  options: {
    logoPlacement: 'none' as const,
    termsPageUrl: `${window.location.origin}${basePath}/terms`,
    privacyPageUrl: `${window.location.origin}${basePath}/privacy`,
  },
  variables: {
    colorPrimary: 'hsl(142, 71%, 45%)',
    colorForeground: 'hsl(0, 0%, 100%)',
    colorMutedForeground: 'hsl(215, 20%, 65%)',
    colorDanger: 'hsl(0, 84%, 60%)',
    colorBackground: 'hsl(215, 61%, 17%)',
    colorInput: 'hsl(215, 45%, 25%)',
    colorInputForeground: 'hsl(0, 0%, 100%)',
    colorNeutral: 'hsl(215, 45%, 25%)',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    borderRadius: '0.5rem',
  },
  elements: {
    // ── Auth screens (sign-in / sign-up) ────────────────────────────────────
    rootBox: 'w-full flex justify-center',
    cardBox: 'bg-[#16335C] rounded-[1.5rem] w-[540px] max-w-full mx-auto overflow-hidden shadow-2xl border border-[#3062A5]',
    card: '!shadow-none !border-0 !bg-transparent !rounded-none !px-6 !pt-8 sm:!px-10 sm:!pt-10',
    footer: '!shadow-none !border-0 !bg-transparent !rounded-none !px-6 !pb-8 sm:!px-10 sm:!pb-10',
    header: 'gap-2',
    headerTitle: 'text-white font-extrabold tracking-tight text-3xl',
    headerSubtitle: 'text-slate-300 font-medium text-base',
    socialButtonsBlockButtonText: 'text-white font-semibold',
    formFieldLabel: 'text-white font-semibold text-sm',
    footerActionLink: 'text-[#5687FF] font-bold hover:underline',
    footerActionText: 'text-slate-400',
    dividerText: 'text-slate-400',
    identityPreviewEditButton: 'text-[#5687FF] hover:underline font-bold',
    formFieldSuccessText: 'text-[#22C55E]',
    alertText: 'text-[#FF7777]',
    socialButtonsBlockButton: '!h-12 !rounded-lg border-[#3062A5] bg-[#1E3E6E] hover:bg-[#21477D] font-semibold text-white',
    formButtonPrimary: '!h-12 !rounded-lg bg-[#22C55E] text-slate-950 hover:bg-[#16A34A] font-bold text-base',
    formFieldInput: '!h-12 !rounded-lg bg-[#0B1B33] border-[#3062A5] text-white !px-4 text-base',
    footerAction: 'justify-center',
    dividerLine: 'bg-[#3062A5]',
    alert: 'bg-[#EF4444]/10 text-[#FF7777] border-[#EF4444]/20',
    otpCodeFieldInput: 'border-[#3062A5] text-white bg-[#0B1B33]',
    formFieldRow: 'gap-5',
    main: 'gap-7',

    // ── UserButton avatar ────────────────────────────────────────────────────
    avatarBox: 'ring-2 ring-[#3062A5] rounded-full',
    userButtonAvatarBox: 'ring-2 ring-[#3062A5] rounded-full',

    // ── UserButton popover card ──────────────────────────────────────────────
    userButtonPopoverCard: 'bg-[#16335C] rounded-xl shadow-xl border border-[#3062A5] overflow-hidden',
    userButtonPopoverMain: 'bg-[#16335C]',
    userButtonPopoverFooter: '!bg-transparent border-t border-[#3062A5]',

    // User preview inside the popover (name + email row)
    userPreviewMainIdentifier: 'text-white font-bold',
    userPreviewSecondaryIdentifier: 'text-slate-300 text-xs',
    userPreviewAvatarBox: 'ring-2 ring-[#3062A5] rounded-full',

    // Action buttons (Manage account, Sign out, etc.)
    userButtonPopoverActionButton: '!rounded-md hover:bg-[#21477D] font-semibold text-white transition-colors',
    userButtonPopoverActionButtonText: 'font-semibold text-white',
    userButtonPopoverActionButtonIcon: 'text-slate-400',

    // ── UserProfile / account-management modal ───────────────────────────────
    modalContent: 'bg-[#16335C] rounded-2xl shadow-2xl border border-[#3062A5] overflow-hidden',
    modalCloseButton: '!rounded-md hover:bg-[#21477D] text-white',

    userProfilePage: 'bg-[#16335C]',
    userProfilePageTitle: 'text-white font-extrabold tracking-tight text-xl',

    navbarButton: '!rounded-md font-semibold text-white hover:bg-[#21477D] data-[active=true]:bg-[#3062A5] data-[active=true]:font-bold transition-colors',

    profileSectionTitle: 'text-white font-bold text-sm tracking-wide uppercase',
    profileSectionPrimaryButton: '!rounded-md bg-[#5687FF] text-white hover:bg-[#4570E6] font-bold',
    profileSectionDangerButton: '!rounded-md border border-[#FF7777] text-[#FF7777] hover:bg-[#FF7777]/10 font-semibold',

    accordionTriggerButton: '!rounded-md hover:bg-[#21477D] font-semibold text-white',
    accordionContent: 'bg-[#0B1B33] rounded-md',

    badge: 'bg-[#3062A5] text-white rounded-full font-semibold text-xs',
  },
};

const PENDING_SCAN_KEY = 'pendingScanUrl';

function AuthPageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center overflow-x-hidden bg-[#102645] px-4 py-8 sm:justify-center sm:py-12">
      {/* Branded header */}
      <Link
        href="/"
        className="relative z-10 mb-6 flex items-center gap-2.5 font-extrabold text-xl tracking-tight text-white hover:opacity-80 transition-opacity sm:mb-8 sm:text-2xl"
        aria-label="ADA Widget home"
      >
        <span aria-hidden="true" className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-[#3062A5] bg-[#16335C] text-[#5687FF]">
          <Accessibility className="h-5 w-5" />
        </span>
        ADA Widget
      </Link>

      {/* Clerk card */}
      <div className="relative z-10 w-full max-w-full">
        {children}
      </div>
    </div>
  );
}

// After sign-in or sign-up Clerk always redirects here so HomeRedirect →
// SignedInHome can pick up any pendingScanUrl stored in localStorage.
// forceRedirectUrl takes precedence over env vars and search params.
// Verified against @clerk/react v6.12.6 / @clerk/shared v4.25.6 types.
const clerkPostAuthUrl = `${basePath}/` || '/';
const SIGNUP_CONSENT_VERSION = '2026-09-02';
const SIGNUP_CONSENT_KEY = `ada-widget-signup-consent:${SIGNUP_CONSENT_VERSION}`;

function SignInPage() {
  return (
    <AuthPageShell>
      <SignIn
        routing="path"
        path={`${basePath}/sign-in`}
        signUpUrl={`${basePath}/sign-up`}
        forceRedirectUrl={clerkPostAuthUrl}
      />
    </AuthPageShell>
  );
}

function SignUpPage() {
  const [hasAcceptedConsent, setHasAcceptedConsent] = useState(() => {
    return sessionStorage.getItem(SIGNUP_CONSENT_KEY) === 'accepted';
  });
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);
  const [acceptedResponsibility, setAcceptedResponsibility] = useState(false);

  function continueToSignUp() {
    if (!acceptedPolicies || !acceptedResponsibility) return;
    sessionStorage.setItem(SIGNUP_CONSENT_KEY, 'accepted');
    setHasAcceptedConsent(true);
  }

  return (
    <AuthPageShell>
      {hasAcceptedConsent ? (
        <SignUp
          routing="path"
          path={`${basePath}/sign-up`}
          signInUrl={`${basePath}/sign-in`}
          forceRedirectUrl={clerkPostAuthUrl}
        />
      ) : (
        <section
          aria-labelledby="signup-consent-heading"
          className="mx-auto w-[540px] max-w-full overflow-hidden rounded-[1.5rem] border border-[#3062A5] bg-[#16335C] px-6 py-8 shadow-2xl sm:px-10 sm:py-10"
          data-testid="signup-consent-gate"
        >
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.12em] text-[#8FA8FF]">Before you create an account</p>
            <h1 id="signup-consent-heading" className="mt-3 text-3xl font-extrabold tracking-tight text-white">
              Review and agree
            </h1>
            <p className="mt-3 text-base leading-7 text-slate-300">
              Please confirm both items below. These acknowledgments apply whether you sign up with email or Google.
            </p>
            <div className="mt-5 rounded-xl border border-[#3B6598] bg-[#102846] px-4 py-3 text-left">
              <p className="text-sm leading-6 text-slate-200">
                A quick note: ADA Widget adds helpful accessibility controls, but it does not rewrite your website’s existing source code. Some code-level accessibility issues may still need to be fixed directly on your site.
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-3 rounded-xl border border-[#3062A5] bg-[#0B1B33] p-4 transition-colors hover:border-[#5687FF]">
              <input
                id="accept-policies"
                type="checkbox"
                checked={acceptedPolicies}
                onChange={(event) => setAcceptedPolicies(event.target.checked)}
                className="mt-1 h-5 w-5 shrink-0 accent-[#22C55E]"
                data-testid="checkbox-accept-policies"
                aria-labelledby="accept-policies-copy"
              />
              <p id="accept-policies-copy" className="text-sm leading-6 text-slate-200">
                By creating an account, I agree to the{' '}
                <a className="font-bold text-[#8FA8FF] underline hover:text-white" href={`${basePath}/terms`} target="_blank" rel="noreferrer">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a className="font-bold text-[#8FA8FF] underline hover:text-white" href={`${basePath}/privacy`} target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
                . I acknowledge that ADA Widget is not legal advice and does not guarantee accessibility compliance or protection from claims or lawsuits.
              </p>
            </div>

            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#3062A5] bg-[#0B1B33] p-4 transition-colors hover:border-[#5687FF]">
              <input
                id="accept-responsibility"
                type="checkbox"
                checked={acceptedResponsibility}
                onChange={(event) => setAcceptedResponsibility(event.target.checked)}
                className="mt-1 h-5 w-5 shrink-0 accent-[#22C55E]"
                data-testid="checkbox-accept-responsibility"
              />
              <span className="text-sm leading-6 text-slate-200">
                I understand that ADA Widget provides automated tools and informational reports only. I remain responsible for my websites, content, legal compliance, remediation decisions, and responses to accessibility complaints or claims.
              </span>
            </label>
          </div>

          <button
            type="button"
            onClick={continueToSignUp}
            disabled={!acceptedPolicies || !acceptedResponsibility}
            className="mt-6 h-12 w-full rounded-lg bg-[#22C55E] px-5 text-base font-bold text-slate-950 transition-colors hover:bg-[#16A34A] disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="button-continue-to-signup"
          >
            Agree and continue
          </button>

          <p className="mt-6 text-center text-sm text-slate-400">
            Already have an account?{' '}
            <Link href="/sign-in" className="font-bold text-[#8FA8FF] hover:text-white">
              Sign in
            </Link>
          </p>
        </section>
      )}
    </AuthPageShell>
  );
}

function LegalConsentRequired({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useGetLegalConsentStatus();
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);
  const [acceptedResponsibility, setAcceptedResponsibility] = useState(false);
  const acceptConsent = useAcceptLegalConsent({
    mutation: {
      onSuccess: (status) => {
        queryClient.setQueryData(getGetLegalConsentStatusQueryKey(), status);
        sessionStorage.removeItem(SIGNUP_CONSENT_KEY);
      },
    },
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[#102645] px-4 text-slate-200">
        Checking your agreement…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[#102645] px-4">
        <section className="w-full max-w-lg rounded-2xl border border-[#3062A5] bg-[#16335C] p-8 text-center text-white">
          <h1 className="text-2xl font-extrabold">We couldn’t verify your agreement</h1>
          <p className="mt-3 text-slate-300">Please try again before continuing to your account.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-6 h-11 rounded-lg bg-[#22C55E] px-6 font-bold text-slate-950 hover:bg-[#16A34A]"
          >
            Try again
          </button>
        </section>
      </div>
    );
  }

  if (data?.accepted) return <>{children}</>;

  return (
    <AuthPageShell>
      <section
        aria-labelledby="legal-consent-heading"
        className="mx-auto w-[540px] max-w-full rounded-[1.5rem] border border-[#3062A5] bg-[#16335C] px-6 py-8 shadow-2xl sm:px-10 sm:py-10"
      >
        <div className="text-center">
          <p className="text-sm font-bold uppercase tracking-[0.12em] text-[#8FA8FF]">
            Agreement required
          </p>
          <h1 id="legal-consent-heading" className="mt-3 text-3xl font-extrabold tracking-tight text-white">
            Review and agree
          </h1>
          <p className="mt-3 text-base leading-7 text-slate-300">
            Please accept the current Terms of Service ({data?.termsVersion}) and Privacy Policy ({data?.privacyVersion}) to continue.
          </p>
        </div>

        <div className="mt-8 space-y-4">
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#3062A5] bg-[#0B1B33] p-4">
            <input
              type="checkbox"
              checked={acceptedPolicies}
              onChange={(event) => setAcceptedPolicies(event.target.checked)}
              className="mt-1 h-5 w-5 shrink-0 accent-[#22C55E]"
            />
            <span className="text-sm leading-6 text-slate-200">
              I agree to the{' '}
              <a className="font-bold text-[#8FA8FF] underline" href={`${basePath}/terms`} target="_blank" rel="noreferrer">
                Terms of Service
              </a>{' '}
              and{' '}
              <a className="font-bold text-[#8FA8FF] underline" href={`${basePath}/privacy`} target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
              .
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#3062A5] bg-[#0B1B33] p-4">
            <input
              type="checkbox"
              checked={acceptedResponsibility}
              onChange={(event) => setAcceptedResponsibility(event.target.checked)}
              className="mt-1 h-5 w-5 shrink-0 accent-[#22C55E]"
            />
            <span className="text-sm leading-6 text-slate-200">
              I understand that ADA Widget provides automated tools and information, not legal advice or a guarantee of accessibility compliance.
            </span>
          </label>
        </div>

        {acceptConsent.isError && (
          <p className="mt-4 text-sm font-semibold text-[#FF7777]">
            We couldn’t save your agreement. Please try again.
          </p>
        )}
        <button
          type="button"
          onClick={() => acceptConsent.mutate()}
          disabled={!acceptedPolicies || !acceptedResponsibility || acceptConsent.isPending}
          className="mt-6 h-12 w-full rounded-lg bg-[#22C55E] px-5 text-base font-bold text-slate-950 hover:bg-[#16A34A] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Agree and continue
        </button>
      </section>
    </AuthPageShell>
  );
}
/**
 * Renders the home page for signed-out users, and for signed-in users either:
 *  - runs the pending scan + shows the loading screen (if pendingScanUrl is set), or
 *  - redirects to /dashboard.
 *
 * Also handles an existing signed-in user who arrives here with a stale
 * pendingScanUrl — same behaviour as a brand-new user.
 */
function HomeRedirect() {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) return null;
  if (!isSignedIn) return <HomePage />;
  return (
    <LegalConsentRequired>
      <SignedInHome />
    </LegalConsentRequired>
  );
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) return null;
  if (!isSignedIn) return <Redirect to="/" />;
  return (
    <LegalConsentRequired>
      <Component />
    </LegalConsentRequired>
  );
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (
        prevUserIdRef.current !== undefined &&
        prevUserIdRef.current !== userId
      ) {
        queryClient.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, queryClient]);

  return null;
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      localization={{
        signIn: {
          start: {
            title: 'Welcome back',
            subtitle: 'Sign in to manage your accessibility widgets',
          },
        },
        signUp: {
          start: {
            title: 'Create your account',
            subtitle: 'Get started with ADA Widget today',
          },
        },
      }}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ClerkQueryClientCacheInvalidator />
          <Switch>
            <Route path="/" component={HomeRedirect} />
            <Route path="/sign-in/*?" component={SignInPage} />
            <Route path="/sign-up/*?" component={SignUpPage} />
            <Route path="/dashboard">
              {() => <ProtectedRoute component={DashboardPage} />}
            </Route>
            <Route path="/sites">
              {() => <ProtectedRoute component={SitesPage} />}
            </Route>
            <Route path="/sites/:siteId">
              {() => <ProtectedRoute component={SiteDetailPage} />}
            </Route>
            <Route path="/sites/:siteId/report/:scanId">
              {() => <ProtectedRoute component={ScanReportPage} />}
            </Route>
            <Route path="/billing">
              {() => <ProtectedRoute component={BillingPage} />}
            </Route>
            <Route path="/settings">
              {() => <ProtectedRoute component={SettingsPage} />}
            </Route>
            <Route path="/terms" component={TermsPage} />
            <Route path="/privacy" component={PrivacyPage} />
            <Route path="/refund" component={RefundPage} />
            <Route component={NotFound} />
          </Switch>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function App() {
  return (
    <WouterRouter base={basePath}>
      <ClerkProviderWithRoutes />
    </WouterRouter>
  );
}

export default App;

/**
 * Module-level flag prevents React StrictMode's double-mount from running the
 * scan handler twice in development. Production is unaffected (single mount).
 */
let pendingScanInFlight = false;

function ScanningLoadingScreen({ domain }: { domain: string }) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-b from-[hsl(var(--pastel-mint))] to-[hsl(var(--pastel-lavender))]">
      {/* Subtle stripe overlay matching the hero */}
      <div className="absolute inset-0 pointer-events-none opacity-20 semrush-stripes" />

      <div className="relative z-10 flex flex-col items-center gap-8 px-6 text-center">
        {/* Animated logo mark */}
        <div className="relative">
          <span className="flex h-20 w-20 items-center justify-center rounded-[1.5rem] bg-foreground text-background shadow-2xl">
            <Accessibility className="h-10 w-10" />
          </span>
          {/* Pulsing ring */}
          <span className="absolute inset-0 rounded-[1.5rem] animate-ping bg-foreground/20" />
        </div>

        <div className="space-y-3">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
            Scanning {domain}&hellip;
          </h1>
          <p className="text-lg font-medium text-foreground/70 max-w-sm">
            Running your free accessibility check. This takes just a moment.
          </p>
        </div>

        {/* Progress bar */}
        <div className="w-64 h-2 rounded-full bg-foreground/10 overflow-hidden">
          <div className="h-full rounded-full bg-foreground/40 animate-[scan-progress_3s_ease-in-out_infinite]" />
        </div>

        <p className="text-sm font-semibold text-foreground/50 uppercase tracking-widest">
          ADA Widget
        </p>
      </div>

      <style>{`
        @keyframes scan-progress {
          0%   { width: 5%;  margin-left: 0; }
          50%  { width: 60%; margin-left: 20%; }
          100% { width: 5%;  margin-left: 95%; }
        }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Signed-in landing: checks for a pending scan and either runs it or redirects
// ---------------------------------------------------------------------------

function SignedInHome() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Read the localStorage key synchronously on first render so the loading
  // screen appears with no blank flash. The key is NOT removed here — it is
  // only removed after site creation succeeds so that a transient network
  // failure leaves it intact for a retry.
  const initialUrlRef = useRef<string | null | undefined>(undefined);
  if (initialUrlRef.current === undefined) {
    const stored = pendingScanInFlight ? null : localStorage.getItem(PENDING_SCAN_KEY);
    initialUrlRef.current = stored ?? null;
    if (stored) {
      pendingScanInFlight = true;
      // localStorage key intentionally NOT removed yet
    }
  }

  const rawUrl = initialUrlRef.current;
  const domain = rawUrl
    ? rawUrl.replace(/^https?:\/\//i, '').replace(/\/+$/, '')
    : null;
  const name = domain ? domain.replace(/^www\./i, '') || domain : null;

  useEffect(() => {
    // No pending URL — just go to the dashboard.
    if (!domain || !name) {
      setLocation('/dashboard', { replace: true } as Parameters<typeof setLocation>[1]);
      return;
    }

    // Outer controller: cancels createSite on unmount (prevents StrictMode
    // double-mount from issuing two createSite requests).
    const controller = new AbortController();
    const { signal } = controller;

    // Scan-step state hoisted to effect scope so the cleanup function can
    // reach them — the async IIFE assigns these after step 1 succeeds.
    let scanController: AbortController | null = null;
    let scanTimerId: ReturnType<typeof setTimeout> | undefined;

    // Tracks whether the async work reached a terminal state. Used by cleanup
    // to decide whether to reset the in-flight flag for a subsequent retry.
    let asyncCompleted = false;

    (async () => {
      // ── Step 1: create the site ──────────────────────────────────────────
      let siteId: number;
      try {
        const site = await createSite({ name, domain }, { signal });
        // Only clear the pending URL once site creation succeeds — on failure
        // the key stays in localStorage so the user can retry without re-typing.
        localStorage.removeItem(PENDING_SCAN_KEY);
        siteId = site.id;
      } catch {
        if (signal.aborted) return; // Unmounted — cleanup handles the flag
        asyncCompleted = true;
        pendingScanInFlight = false;
        toast({
          title: "We couldn't start your scan",
          description: 'Check your connection then tap "Try again".',
          variant: 'destructive',
          action: (
            <ToastAction
              altText="Try again"
              onClick={() =>
                setLocation('/', { replace: true } as Parameters<typeof setLocation>[1])
              }
            >
              Try again
            </ToastAction>
          ),
        });
        setLocation('/dashboard', { replace: true } as Parameters<typeof setLocation>[1]);
        return;
      }

      if (signal.aborted) { asyncCompleted = true; return; }

      // ── Step 2: run the scan with a 30-second timeout ────────────────────
      //
      // A dedicated scanController keeps the timeout-triggered abort separate
      // from the unmount-triggered abort (outer signal). This matters in the
      // catch block: signal.aborted is the unmount test; scanController being
      // aborted without signal.aborted means the timeout fired.
      scanController = new AbortController();

      const scanPromise = runScan(siteId, { signal: scanController.signal });
      // Suppress the unhandled-rejection that appears when the timeout wins
      // and scanPromise eventually rejects with an AbortError.
      scanPromise.catch(() => {});

      const timeoutPromise = new Promise<never>((_, reject) => {
        scanTimerId = setTimeout(() => {
          scanController!.abort();           // cancel the in-flight request
          reject(new Error('SCAN_TIMEOUT')); // lose the race
        }, 30_000);
      });

      try {
        const scan = await Promise.race([scanPromise, timeoutPromise]);
        clearTimeout(scanTimerId);           // scan won — kill the timer
        if (signal.aborted) { asyncCompleted = true; return; }
        asyncCompleted = true;
        pendingScanInFlight = false;
        setLocation(
          `/sites/${siteId}/report/${scan.id}`,
          { replace: true } as Parameters<typeof setLocation>[1],
        );
      } catch (err) {
        clearTimeout(scanTimerId);
        // Outer signal aborted = component unmounted; bail silently.
        // (If the timeout fired, outer signal is still intact.)
        if (signal.aborted) { asyncCompleted = true; return; }
        asyncCompleted = true;
        pendingScanInFlight = false;
        const isTimeout = err instanceof Error && err.message === 'SCAN_TIMEOUT';
        toast({
          title: isTimeout
            ? 'Your scan is taking longer than expected'
            : "We couldn't complete the scan",
          description: "You can retry the scan from your site's page.",
          variant: 'destructive',
        });
        // Site was created — land on its page so the user can retry there.
        setLocation(`/sites/${siteId}`, { replace: true } as Parameters<typeof setLocation>[1]);
      }
    })();

    return () => {
      controller.abort();        // cancel createSite / outer abort
      scanController?.abort();   // cancel in-flight runScan if started
      clearTimeout(scanTimerId); // kill the dangling 30 s timer if set
      // If async never reached a terminal state (StrictMode re-mount, tab
      // close mid-flight, etc.) reset the flag so the next mount can retry.
      if (!asyncCompleted) {
        pendingScanInFlight = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain]);

  if (domain) {
    return <ScanningLoadingScreen domain={domain} />;
  }

  // Render nothing while the redirect in the effect fires.
  return null;
}
